import dayjs from "dayjs";
import { useCallback, useMemo, useRef, useState } from "react";

import { useLocationContext } from "@/contexts/LocationContext";

import {
  applyPatientFinderFilters,
  type PatientFinderCriteria,
  type PatientFinderGender,
  type PatientFinderCommunicationFilter,
} from "../aggregations";
import { applyPatientFinderAppointmentMode } from "../helpers/applyPatientFinderAppointmentMode";
import { buildLocationActorFromContext } from "../helpers/buildLocationActorFromContext";
import { deriveLastAppointmentByPatientId } from "../helpers/deriveLastAppointmentByPatientId";
import { derivePatientIdsAttendedInPeriod } from "../helpers/derivePatientIdsAttendedInPeriod";
import { enrichPatientsWithLastAppointments } from "../helpers/enrichPatientsWithLastAppointments";
import {
  defaultPatientFinderAppointmentMode,
  type PatientFinderAppointmentMode,
} from "../helpers/patientFinderAppointmentMode";
import {
  type AppointmentPeriodBounds,
  resolveAppointmentFetchDateRange,
} from "../helpers/resolveAppointmentFetchDateRange";
import { fetchLocationAppointments } from "../services/fetchLocationAppointments";
import { AppointmentSearchVolumeTooHighError } from "../helpers/appointmentSearchVolumeLimit";
import type { CrmPatientSummary } from "../types";

export interface PatientFinderSearchCriteria {
  gender: PatientFinderGender;
  communicationFilter: PatientFinderCommunicationFilter;
  communicatedPatientIds: Set<string>;
  minAgeYears: number | null;
  maxAgeYears: number | null;
  refDate: Date;
}

export interface PatientFinderAppointmentQuery {
  mode: PatientFinderAppointmentMode;
  period: AppointmentPeriodBounds;
}

export interface PatientFinderSearchInput {
  /** Quando false, ignora consultas FHIR e usa só demais filtros. */
  useAppointmentFilter: boolean;
  appointmentQuery?: PatientFinderAppointmentQuery;
}

export interface UseCommunicationsPatientFinderSearchResult {
  isSearching: boolean;
  searchError: string | null;
  isLocationLoading: boolean;
  hasLocationActor: boolean;
  searchPatients: (
    allPatients: CrmPatientSummary[],
    input: PatientFinderSearchInput,
    criteria: PatientFinderSearchCriteria,
  ) => Promise<CrmPatientSummary[]>;
  resetSearch: () => void;
}

function applyLocalPatientFinderFilters(
  patients: CrmPatientSummary[],
  criteria: PatientFinderSearchCriteria,
): CrmPatientSummary[] {
  return applyPatientFinderFilters(patients, {
    ...criteria,
    lastAppointmentFrom: null,
    lastAppointmentTo: null,
  });
}

/**
 * Busca opcional por consultas + filtros locais (sexo, comunicação, idade).
 */
export function useCommunicationsPatientFinderSearch(): UseCommunicationsPatientFinderSearchResult {
  const { location: contextLocation, locationLoading } = useLocationContext();

  const locationActor = useMemo(
    () => buildLocationActorFromContext(contextLocation),
    [contextLocation],
  );

  const hasLocationActor = Boolean(locationActor?.trim());

  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const resetSearch = useCallback(() => {
    requestIdRef.current += 1;
    setIsSearching(false);
    setSearchError(null);
  }, []);

  const searchPatients = useCallback(
    async (
      allPatients: CrmPatientSummary[],
      input: PatientFinderSearchInput,
      criteria: PatientFinderSearchCriteria,
    ): Promise<CrmPatientSummary[]> => {
      const requestId = ++requestIdRef.current;
      setIsSearching(true);
      setSearchError(null);

      try {
        if (!input.useAppointmentFilter) {
          if (requestId !== requestIdRef.current) return [];
          return applyLocalPatientFinderFilters(allPatients, criteria);
        }

        const query = input.appointmentQuery;
        if (!query) {
          setSearchError("Configuração de consulta inválida.");
          return [];
        }

        const periodFrom = query.period.from?.trim() || null;
        const periodTo = query.period.to?.trim() || null;

        if (!periodFrom && !periodTo) {
          setSearchError(
            query.mode === "scheduled"
              ? "Com filtro de consultas ativo, informe a data a partir da qual deseja buscar."
              : "Com filtro de consultas ativo, informe ao menos a data de início ou a data fim.",
          );
          return [];
        }

        if (locationLoading) {
          setSearchError("Aguarde o carregamento das unidades da clínica.");
          return [];
        }

        const actor = locationActor?.trim();
        if (!actor) {
          setSearchError(
            "Nenhuma unidade (Location) encontrada. Desative o filtro de consultas ou verifique seu acesso.",
          );
          return [];
        }

        const fetchRange = resolveAppointmentFetchDateRange(
          query.period,
          query.mode,
        );
        if (!fetchRange) {
          setSearchError("Período de consultas inválido.");
          return [];
        }

        const bundle = await fetchLocationAppointments({
          locationActor: actor,
          startDate: fetchRange.startDate,
          endDate: fetchRange.endDate,
          mode: query.mode,
        });

        if (requestId !== requestIdRef.current) {
          return [];
        }

        const matchingIds = derivePatientIdsAttendedInPeriod(bundle, query.period);
        const byAppointmentMode = applyPatientFinderAppointmentMode(
          allPatients,
          matchingIds,
          query.mode,
        );

        const lastByPatient = deriveLastAppointmentByPatientId(bundle);
        const enriched = enrichPatientsWithLastAppointments(
          byAppointmentMode,
          lastByPatient,
        );

        return applyLocalPatientFinderFilters(enriched, criteria);
      } catch (error) {
        if (requestId !== requestIdRef.current) {
          return [];
        }
        if (error instanceof AppointmentSearchVolumeTooHighError) {
          setSearchError(error.message);
          return [];
        }
        setSearchError(
          "Não foi possível carregar as consultas. Tente novamente.",
        );
        return [];
      } finally {
        if (requestId === requestIdRef.current) {
          setIsSearching(false);
        }
      }
    },
    [locationActor, locationLoading],
  );

  return {
    isSearching,
    searchError,
    isLocationLoading: locationLoading,
    hasLocationActor,
    searchPatients,
    resetSearch,
  };
}

export interface PatientFinderFormDefaults {
  useAppointmentFilter: boolean;
  appointmentMode: PatientFinderAppointmentMode;
  /** Sem valor até o usuário informar início e/ou fim. */
  periodFrom: dayjs.Dayjs | null;
  periodTo: dayjs.Dayjs | null;
  gender: PatientFinderGender;
  communicationFilter: PatientFinderCommunicationFilter;
  minAge: string;
  maxAge: string;
}

/** Padrão: nenhum filtro ativo — lista todos os pacientes ao clicar em Listar. */
export function getDefaultPatientFinderFormState(): PatientFinderFormDefaults {
  return {
    useAppointmentFilter: false,
    appointmentMode: defaultPatientFinderAppointmentMode(),
    periodFrom: null,
    periodTo: null,
    gender: "all",
    communicationFilter: "all",
    minAge: "",
    maxAge: "",
  };
}

export function dayjsToIsoDate(value: dayjs.Dayjs | null): string | null {
  if (!value || !value.isValid()) return null;
  return value.format("YYYY-MM-DD");
}
